import { Link } from "wouter";
import { ArrowLeft } from "lucide-react";
import { useEffect } from "react";

export default function PrivacyPolicy() {
  useEffect(() => {
    document.title = "Privacy Policy | Drpr.ai";
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-[#0A0A0F] text-foreground" data-testid="privacy-policy-page">
      <header className="border-b border-foreground/10 px-6 py-4">
        <div className="max-w-4xl mx-auto flex items-center justify-between gap-4 flex-wrap">
          <Link href="/" data-testid="link-home">
            <span className="text-xl font-bold tracking-tight cursor-pointer">Drpr.ai</span>
          </Link>
          <Link href="/" data-testid="link-back-home">
            <span className="flex items-center gap-2 text-sm text-foreground/60 hover:text-foreground transition-colors cursor-pointer">
              <ArrowLeft size={16} />
              Back to Home
            </span>
          </Link>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-12">
        <div className="space-y-2 mb-12">
          <h1 className="text-3xl md:text-4xl font-bold" data-testid="text-privacy-title">Privacy Policy</h1>
          <p className="text-foreground/50 text-sm">Last Updated: February 16, 2026</p>
        </div>

        <div className="space-y-10 text-foreground/80 leading-relaxed text-[15px]">
          <section className="space-y-4">
            <h2 className="text-xl font-semibold text-foreground">1. Introduction</h2>
            <p>
              Welcome to Drpr.ai, an autonomous AI marketing SaaS platform operated by Lejjors Corp.
              ("Company," "we," "us," or "our"). This Privacy Policy explains how we collect, use,
              disclose, and safeguard your information when you use our platform at drpr.ai (the "Service").
            </p>
            <p>
              By accessing or using the Service, you agree to the terms of this Privacy Policy. If you
              do not agree with the terms of this Privacy Policy, please do not access the Service.
            </p>
          </section>

          <section className="space-y-4">
            <h2 className="text-xl font-semibold text-foreground">2. Information We Collect</h2>

            <h3 className="text-lg font-medium text-foreground/90">2.1 Personal Information</h3>
            <p>We may collect the following personal information when you register for and use our Service:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Name, email address, and contact information</li>
              <li>Business name, industry, and business-related information</li>
              <li>Account credentials and authentication data</li>
              <li>Billing and payment information (processed through Stripe)</li>
              <li>Profile information and preferences</li>
            </ul>

            <h3 className="text-lg font-medium text-foreground/90">2.2 Business and Marketing Data</h3>
            <p>Through your use of the Service, we collect and process:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Marketing strategies, campaigns, and content you create</li>
              <li>Ad campaign data, performance metrics, and analytics</li>
              <li>Lead information and customer data you manage through the platform</li>
              <li>Landing pages, funnels, and published content</li>
              <li>AI-generated content and strategy recommendations</li>
            </ul>

            <h3 className="text-lg font-medium text-foreground/90">2.3 Social Media Account Data</h3>
            <p>
              When you connect your social media accounts, we collect data from the following platforms
              in accordance with their respective API terms:
            </p>
            <ul className="list-disc pl-6 space-y-2">
              <li>
                <strong className="text-foreground">Meta (Facebook/Instagram):</strong> Page information,
                post data, ad account details, audience insights, and engagement metrics via the
                Meta Graph API
              </li>
              <li>
                <strong className="text-foreground">LinkedIn:</strong> Company page data, post content,
                and professional audience analytics via the LinkedIn API
              </li>
              <li>
                <strong className="text-foreground">X (formerly Twitter):</strong> Account information,
                tweet data, and engagement metrics via the X API
              </li>
            </ul>

            <h3 className="text-lg font-medium text-foreground/90">2.4 Automatically Collected Information</h3>
            <ul className="list-disc pl-6 space-y-2">
              <li>IP address, browser type, and device information</li>
              <li>Usage data, access times, and pages viewed</li>
              <li>Referring URLs and navigation patterns</li>
              <li>Log data and error reports</li>
            </ul>
          </section>

          <section className="space-y-4">
            <h2 className="text-xl font-semibold text-foreground">3. How We Use Your Information</h2>
            <p>We use the information we collect for the following purposes:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li>To provide, maintain, and improve the Service</li>
              <li>To create and manage your account</li>
              <li>To generate AI-powered marketing strategies, content, and ad campaigns using Google Gemini AI</li>
              <li>To publish and manage content across connected social media platforms</li>
              <li>To manage and optimize advertising campaigns</li>
              <li>To process leads and manage your sales funnel</li>
              <li>To process payments and manage subscriptions through Stripe</li>
              <li>To send you service-related communications and updates</li>
              <li>To provide customer support and respond to inquiries</li>
              <li>To analyze usage patterns and improve user experience</li>
              <li>To detect, prevent, and address technical issues and security threats</li>
              <li>To comply with legal obligations</li>
            </ul>
          </section>

          <section className="space-y-4">
            <h2 className="text-xl font-semibold text-foreground">4. Third-Party Services and Data Sharing</h2>
            <p>
              We share your information with the following third-party services as necessary to provide the Service:
            </p>

            <h3 className="text-lg font-medium text-foreground/90">4.1 Google Gemini AI</h3>
            <p>
              We use Google Gemini AI to power our autonomous marketing features including strategy
              generation, content creation, and campaign optimization. Your business information and
              marketing data may be processed by Google's AI services in accordance with
              Google's privacy policies and our data processing agreement.
            </p>

            <h3 className="text-lg font-medium text-foreground/90">4.2 Meta (Facebook/Instagram)</h3>
            <p>
              When you connect your Meta accounts, we access and use the Meta Graph API to manage
              your Facebook Pages, Instagram accounts, and advertising campaigns. Data shared with
              Meta is governed by Meta's Platform Terms and Data Policy.
            </p>

            <h3 className="text-lg font-medium text-foreground/90">4.3 LinkedIn</h3>
            <p>
              When you connect your LinkedIn account, we use the LinkedIn API to manage company page
              content and professional marketing activities. Data shared with LinkedIn is subject to
              LinkedIn's User Agreement and Privacy Policy.
            </p>

            <h3 className="text-lg font-medium text-foreground/90">4.4 X (formerly Twitter)</h3>
            <p>
              When you connect your X account, we use the X API to manage posts and engagement on
              your behalf. Data shared with X is governed by X's Terms of Service and Privacy Policy.
            </p>

            <h3 className="text-lg font-medium text-foreground/90">4.5 Stripe</h3>
            <p>
              We use Stripe to process payments and manage subscriptions. Your payment information is
              collected and processed directly by Stripe and is subject to Stripe's Privacy Policy.
              We do not store your full credit card information on our servers.
            </p>

            <h3 className="text-lg font-medium text-foreground/90">4.6 Other Disclosures</h3>
            <p>We may also disclose your information:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li>To comply with applicable laws, regulations, or legal processes</li>
              <li>To respond to lawful requests from public authorities</li>
              <li>To protect our rights, privacy, safety, or property</li>
              <li>In connection with a merger, acquisition, or sale of assets</li>
              <li>With your consent or at your direction</li>
            </ul>
          </section>

          <section className="space-y-4">
            <h2 className="text-xl font-semibold text-foreground">5. Data Storage and Security</h2>
            <p>
              Your data is stored in a PostgreSQL database hosted on secure infrastructure. We implement
              appropriate technical and organizational security measures to protect your personal
              information, including:
            </p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Encryption of data in transit using TLS/SSL</li>
              <li>Secure authentication and access controls</li>
              <li>Regular security assessments and monitoring</li>
              <li>Access limited to authorized personnel only</li>
              <li>Secure API key management and token encryption</li>
            </ul>
            <p>
              While we strive to use commercially acceptable means to protect your personal information,
              no method of transmission over the Internet or method of electronic storage is 100% secure.
            </p>
          </section>

          <section className="space-y-4">
            <h2 className="text-xl font-semibold text-foreground">6. Cookies and Tracking Technologies</h2>
            <p>We use cookies and similar tracking technologies to:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Maintain your session and authentication state</li>
              <li>Remember your preferences and settings</li>
              <li>Analyze usage patterns and improve the Service</li>
              <li>Provide and measure the effectiveness of features</li>
            </ul>
            <p>
              You can instruct your browser to refuse all cookies or to indicate when a cookie is
              being sent. However, if you do not accept cookies, you may not be able to use some
              portions of the Service.
            </p>
          </section>

          <section className="space-y-4">
            <h2 className="text-xl font-semibold text-foreground">7. Data Retention</h2>
            <p>
              We retain your personal information for as long as your account is active or as needed
              to provide you with the Service. We will also retain and use your information as necessary
              to comply with legal obligations, resolve disputes, and enforce our agreements.
            </p>
            <p>
              When you delete your account, we will delete or anonymize your personal information
              within 30 days, except where we are required to retain it by law or for legitimate
              business purposes such as fraud prevention.
            </p>
            <p>
              Marketing content, campaign data, and analytics associated with your connected social
              media accounts may be retained by those platforms according to their own data retention policies.
            </p>
          </section>

          <section className="space-y-4">
            <h2 className="text-xl font-semibold text-foreground">8. Your Rights and Choices</h2>
            <p>Depending on your location, you may have the following rights regarding your personal information:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li><strong className="text-foreground">Access:</strong> Request a copy of the personal data we hold about you</li>
              <li><strong className="text-foreground">Correction:</strong> Request correction of inaccurate or incomplete data</li>
              <li><strong className="text-foreground">Deletion:</strong> Request deletion of your personal data</li>
              <li><strong className="text-foreground">Portability:</strong> Request a copy of your data in a portable format</li>
              <li><strong className="text-foreground">Objection:</strong> Object to certain processing of your data</li>
              <li><strong className="text-foreground">Restriction:</strong> Request restriction of processing of your data</li>
              <li><strong className="text-foreground">Withdraw Consent:</strong> Withdraw consent where processing is based on consent</li>
            </ul>
            <p>
              To exercise any of these rights, please contact us at admin@lejjors.com. We will respond
              to your request within 30 days.
            </p>
          </section>

          <section className="space-y-4">
            <h2 className="text-xl font-semibold text-foreground">9. GDPR Compliance (European Users)</h2>
            <p>
              If you are located in the European Economic Area (EEA), United Kingdom, or Switzerland,
              you have certain rights under the General Data Protection Regulation (GDPR):
            </p>
            <ul className="list-disc pl-6 space-y-2">
              <li>We process your data based on contractual necessity, legitimate interests, consent, or legal obligations</li>
              <li>You have the right to lodge a complaint with your local data protection authority</li>
              <li>We will only transfer your data outside the EEA with appropriate safeguards in place</li>
              <li>You may exercise your data subject rights as described in Section 8 above</li>
            </ul>
            <p>
              Our legal bases for processing personal data include: performance of our contract with
              you, our legitimate business interests, your consent, and compliance with legal obligations.
            </p>
          </section>

          <section className="space-y-4">
            <h2 className="text-xl font-semibold text-foreground">10. CCPA Compliance (California Residents)</h2>
            <p>
              If you are a California resident, you have specific rights under the California Consumer
              Privacy Act (CCPA) and California Privacy Rights Act (CPRA):
            </p>
            <ul className="list-disc pl-6 space-y-2">
              <li><strong className="text-foreground">Right to Know:</strong> You can request disclosure of the categories and specific pieces of personal information we have collected about you</li>
              <li><strong className="text-foreground">Right to Delete:</strong> You can request deletion of your personal information, subject to certain exceptions</li>
              <li><strong className="text-foreground">Right to Opt-Out:</strong> You can opt out of the sale or sharing of your personal information. We do not sell personal information</li>
              <li><strong className="text-foreground">Right to Non-Discrimination:</strong> We will not discriminate against you for exercising your CCPA rights</li>
              <li><strong className="text-foreground">Right to Correct:</strong> You can request correction of inaccurate personal information</li>
            </ul>
            <p>
              To exercise your CCPA rights, contact us at admin@lejjors.com or visit our platform settings.
              We will verify your identity before processing your request.
            </p>
          </section>

          <section className="space-y-4">
            <h2 className="text-xl font-semibold text-foreground">11. Canadian Privacy Law Compliance</h2>
            <p>
              For users in Canada, we comply with the Personal Information Protection and Electronic
              Documents Act (PIPEDA) and applicable provincial privacy legislation. You have the right
              to access, correct, and request deletion of your personal information. We obtain meaningful
              consent for the collection, use, and disclosure of personal information.
            </p>
          </section>

          <section className="space-y-4">
            <h2 className="text-xl font-semibold text-foreground">12. Children's Privacy</h2>
            <p>
              The Service is not intended for individuals under the age of 18. We do not knowingly
              collect personal information from children under 18. If we learn that we have collected
              personal information from a child under 18, we will take steps to delete such information
              promptly. If you believe we have inadvertently collected information from a child under 18,
              please contact us at admin@lejjors.com.
            </p>
          </section>

          <section className="space-y-4">
            <h2 className="text-xl font-semibold text-foreground">13. Social Media Platform Data Usage</h2>
            <p>
              When you connect third-party social media accounts to Drpr.ai, we access data through
              their official APIs. Specifically:
            </p>
            <ul className="list-disc pl-6 space-y-2">
              <li>We only request permissions necessary for the features you use</li>
              <li>We do not sell data obtained from social media platforms</li>
              <li>Data obtained from Meta, LinkedIn, and X APIs is used solely to provide our marketing automation services</li>
              <li>You can disconnect your social media accounts at any time through your account settings, which will stop further data collection from those platforms</li>
              <li>We comply with each platform's API terms of service, rate limits, and data handling requirements</li>
              <li>Data obtained from these platforms is not used for purposes unrelated to the Service</li>
            </ul>
          </section>

          <section className="space-y-4">
            <h2 className="text-xl font-semibold text-foreground">14. AI Data Processing</h2>
            <p>
              Our Service uses Google Gemini AI to provide autonomous marketing capabilities. When using
              AI features:
            </p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Your business information and marketing data may be sent to Google's AI services for processing</li>
              <li>AI-generated content is created based on your inputs and business context</li>
              <li>We do not use your data to train AI models</li>
              <li>AI processing is subject to Google's data handling and privacy practices</li>
              <li>You retain ownership of all content generated through the Service</li>
            </ul>
          </section>

          <section className="space-y-4">
            <h2 className="text-xl font-semibold text-foreground">15. Changes to This Privacy Policy</h2>
            <p>
              We may update this Privacy Policy from time to time. We will notify you of any material
              changes by posting the new Privacy Policy on this page and updating the "Last Updated"
              date. We may also notify you by email for significant changes. Your continued use of the
              Service after changes are posted constitutes your acceptance of the updated Privacy Policy.
            </p>
          </section>

          <section className="space-y-4">
            <h2 className="text-xl font-semibold text-foreground">16. Contact Us</h2>
            <p>
              If you have any questions about this Privacy Policy, your personal data, or wish to
              exercise your privacy rights, please contact us:
            </p>
            <div className="bg-foreground/5 border border-foreground/10 rounded-md p-6 space-y-2">
              <p><strong className="text-foreground">Lejjors Corp.</strong></p>
              <p>Product: Drpr.ai</p>
              <p>Email: <a href="mailto:admin@lejjors.com" className="text-blue-400 hover:underline" data-testid="link-contact-email">admin@lejjors.com</a></p>
              <p>Website: <a href="https://drpr.ai" className="text-blue-400 hover:underline" data-testid="link-website">drpr.ai</a></p>
            </div>
          </section>
        </div>

        <div className="mt-16 pt-8 border-t border-foreground/10 flex items-center justify-between gap-4 flex-wrap text-sm text-foreground/40">
          <p>&copy; {new Date().getFullYear()} Lejjors Corp. All rights reserved.</p>
          <div className="flex items-center gap-4">
            <Link href="/terms" data-testid="link-terms">
              <span className="hover:text-foreground/70 transition-colors cursor-pointer">Terms of Service</span>
            </Link>
            <Link href="/privacy" data-testid="link-privacy">
              <span className="hover:text-foreground/70 transition-colors cursor-pointer">Privacy Policy</span>
            </Link>
          </div>
        </div>
      </main>
    </div>
  );
}